import React, { useEffect } from 'react';
import styles from '../../Personal/Personal.module.scss';
import { Button, Flex, Form, Input } from 'antd';
import CustomButton from '../../../../components/UI/CustomButton/CustomButton';
import { ReactComponent as LogoIcon } from '../../../../assets/images/logo/logo.svg';
import { ReactComponent as EmailIcon } from '../../../../assets/images/global-icons/mail_ic.svg';
import { ReactComponent as PassIcon } from '../../../../assets/images/global-icons/lock_ic.svg';
import { ReactComponent as OpenEyeIcon } from '../../../../assets/images/global-icons/eye_ic.svg';
import { ReactComponent as GoogleIcon } from '../../../../assets/images/global-icons/google_ic.svg';
import { ReactComponent as AppleIcon } from '../../../../assets/images/global-icons/apple_ic.svg';
import { ReactComponent as CloseEyeIcon } from '../../../../assets/images/global-icons/eye_crossed_ic.svg';
import useCreateBusinessForm from '../../../../hooks/useCreateBusinessForm';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  useAppleAuthMutation,
  useGoogleAuthMutation,
  useSignUpMutation,
  useUpdateProfileMutation
} from '../../../../redux/API/userSlice';
import { addSignUpData } from '../../../../redux/businessSlice';
import {
  useCreateCompanyMutation,
  useSendJoinRequestMutation,
  useSendVerificationCompanyMailMutation
} from '../../../../redux/API/companySlice';
import { useGoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

const Email = () => {
  const { t } = useTranslation();
  const savedForm = useSelector((state: RootState) => state.business);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [signUp] = useSignUpMutation();
  const [authGoogle] = useGoogleAuthMutation();
  const [createCompany] = useCreateCompanyMutation();
  const [sendJoinRequest] = useSendJoinRequestMutation();
  const [sendVerificationCompanyMail] = useSendVerificationCompanyMailMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const [authApple] = useAppleAuthMutation();
  useEffect(() => {
    form.resetFields();
  }, []);
  const { form, onFinish, passwordValidator, isDisabledBtn, values } = useCreateBusinessForm();

  const initialValues = {
    email: '',
    password: ''
  };

  const handleSubmit = async () => {
    try {
      await onFinish();

      const signUpData = await signUp({
        email: values.email,
        password: values.password,
        accountType: 'business'
      }).unwrap();

      dispatch(
        addSignUpData({
          accessToken: signUpData?.data.token.accessToken
        })
      );

      if (!savedForm.company.id) {
        const createdCompany = await createCompany({
          body: {
            companyName: savedForm.company.companyName,
            address: savedForm.company.address,
            phone: '+1-555-123-4567',
            website: savedForm.company.website ?? 'website',
            dunsNumber: '123456789',
            placeId: savedForm.company.placeId,
            email: savedForm.company.email,
            link: `https://linkedin.com/company/${savedForm.company.companyName}`,
            pictures: savedForm.company.pictures,
            isValidate: false,
            description: ''
          },
          accessToken: signUpData?.data.token.accessToken
        }).unwrap();

        const updateProfileData = {
          company: {
            id: createdCompany?.data?.id,
            companyName: createdCompany?.data?.companyName,
            address: createdCompany?.data?.address,
            phone: createdCompany?.data?.phone,
            website: createdCompany?.data?.website,
            dunsNumber: createdCompany?.data?.dunsNumber,
            placeId: createdCompany?.data?.placeId,
            isValidate: createdCompany?.data?.isValidated,
            description: createdCompany?.data?.description,
            email: createdCompany?.data?.email,
            link: createdCompany?.data?.link,
            picture: createdCompany?.data?.picture
          }
        };

        await updateProfile({
          body: updateProfileData,
          id: signUpData?.data.user.id,
          accessToken: signUpData?.data.token.accessToken
        }).unwrap();

        await sendJoinRequest({
          body: {
            userId: signUpData?.data.user.id,
            companyId: createdCompany?.data.id
          },
          accessToken: signUpData?.data.token.accessToken
        }).unwrap();

        await sendVerificationCompanyMail({
          name: savedForm.company.companyName,
          email: savedForm.company.email
        }).unwrap();
      } else {
        await sendJoinRequest({
          body: {
            userId: signUpData?.data.user.id,
            companyId: savedForm.company.id
          },
          accessToken: signUpData?.data.token.accessToken
        }).unwrap();

        await sendVerificationCompanyMail({
          name: savedForm.company.companyName,
          email: savedForm.company.email
        }).unwrap();
      }

      navigate('../business/phone');
    } catch (error: any) {
      console.error(error);

      const errorMessage = error?.data?.error?.description || 'An error occurred';

      form.setFields([
        {
          name: 'password',
          errors: [errorMessage]
        }
      ]);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const result = await authGoogle({
          code: tokenResponse.access_token,
          accountType: 'business'
        }).unwrap();
        console.log('Google Login Success:', tokenResponse);

        dispatch(
          addSignUpData({
            accessToken: result.data?.token?.accessToken
          })
        );

        if (!savedForm.company.id) {
          const createdCompany = await createCompany({
            body: {
              companyName: savedForm.company.companyName,
              address: savedForm.company.address,
              phone: '+1-555-123-4567',
              website: savedForm.company.website ?? 'website',
              dunsNumber: '123456789',
              placeId: savedForm.company.placeId,
              email: savedForm.company.email,
              link: `https://linkedin.com/company/${savedForm.company.companyName}`,
              pictures: savedForm.company.pictures,
              isValidate: false,
              description: ''
            },
            accessToken: result?.data.token.accessToken
          }).unwrap();

          const updateProfileData = {
            company: {
              id: createdCompany?.data?.id,
              companyName: createdCompany?.data?.companyName,
              address: createdCompany?.data?.address,
              phone: createdCompany?.data?.phone,
              website: createdCompany?.data?.website,
              dunsNumber: createdCompany?.data?.dunsNumber,
              placeId: createdCompany?.data?.placeId,
              isValidate: createdCompany?.data?.isValidated,
              description: createdCompany?.data?.description,
              email: createdCompany?.data?.email,
              link: createdCompany?.data?.link,
              picture: createdCompany?.data?.picture
            }
          };

          await updateProfile({
            body: updateProfileData,
            id: result?.data.user.id,
            accessToken: result?.data.token.accessToken
          }).unwrap();

          await sendJoinRequest({
            body: {
              userId: result?.data.user.id,
              companyId: createdCompany?.data.id
            },
            accessToken: result?.data.token.accessToken
          }).unwrap();

          await sendVerificationCompanyMail({
            name: savedForm.company.companyName,
            email: savedForm.company.email
          }).unwrap();
        } else {
          await sendJoinRequest({
            body: {
              userId: result?.data.user.id,
              companyId: savedForm.company.id
            },
            accessToken: result?.data.token.accessToken
          }).unwrap();

          await sendVerificationCompanyMail({
            name: savedForm.company.companyName,
            email: savedForm.company.email
          }).unwrap();
        }

        navigate('../business/phone');
      } catch (err) {}
    },
    onError: (error) => console.log('Login from Google Failed', error)
  });

  const handleAppleSuccess = async (response: any) => {
    console.log('Apple Sign-In Success:', response);

    const { authorization } = response;
    if (!authorization?.id_token) {
      throw new Error('Apple authentication failed. No token received.');
    }

    try {
      const res = await authApple({
        idToken: authorization.id_token,
        email: response?.user?.email ?? '',
        firstName: response?.user?.name?.firstName ?? '',
        lastName: response?.user?.name?.lastName ?? ''
      }).unwrap();

      dispatch(
        addSignUpData({
          accessToken: res.data?.token?.accessToken
        })
      );

      if (!savedForm.company.id) {
        const createdCompany = await createCompany({
          body: {
            companyName: savedForm.company.companyName,
            address: savedForm.company.address,
            phone: '+1-555-123-4567',
            website: savedForm.company.website ?? 'website',
            dunsNumber: '123456789',
            placeId: savedForm.company.placeId,
            email: savedForm.company.email,
            link: `https://linkedin.com/company/${savedForm.company.companyName}`,
            pictures: savedForm.company.pictures,
            isValidate: false,
            description: ''
          },
          accessToken: res?.data.token.accessToken
        }).unwrap();

        const updateProfileData = {
          company: {
            id: createdCompany?.data?.id,
            companyName: createdCompany?.data?.companyName,
            address: createdCompany?.data?.address,
            phone: createdCompany?.data?.phone,
            website: createdCompany?.data?.website,
            dunsNumber: createdCompany?.data?.dunsNumber,
            placeId: createdCompany?.data?.placeId,
            isValidate: createdCompany?.data?.isValidated,
            description: createdCompany?.data?.description,
            email: createdCompany?.data?.email,
            link: createdCompany?.data?.link,
            picture: createdCompany?.data?.picture
          }
        };

        await updateProfile({
          body: updateProfileData,
          id: res?.data.user.id,
          accessToken: res?.data.token.accessToken
        }).unwrap();

        await sendJoinRequest({
          body: {
            userId: res?.data.user.id,
            companyId: createdCompany?.data.id
          },
          accessToken: res?.data.token.accessToken
        }).unwrap();

        await sendVerificationCompanyMail({
          name: savedForm.company.companyName,
          email: savedForm.company.email
        }).unwrap();
      } else {
        await sendJoinRequest({
          body: {
            userId: res?.data.user.id,
            companyId: savedForm.company.id
          },
          accessToken: res?.data.token.accessToken
        }).unwrap();

        await sendVerificationCompanyMail({
          name: savedForm.company.companyName,
          email: savedForm.company.email
        }).unwrap();
      }

      navigate('../business/phone');
    } catch (error) {
      console.error('Apple Auth Error:', error);
    }
  };

  const handleAppleError = (error: any) => {
    console.error('Apple Sign-In Error:', error);
  };

  return (
    <Flex className={styles.Personal__wrapper} justify={'center'} align={'center'}>
      <Flex className={styles.Personal} vertical>
        <Flex className={styles.Personal__header} vertical justify={'center'} align={'center'} gap={'32px'}>
          <LogoIcon />
          <h1>Create New Account</h1>
        </Flex>
        <Form
          className={styles.Personal__form}
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Your Email"
            name="email"
            rules={[
              {
                required: true,
                type: 'email',
                message: t('Invalid email format')
              }
            ]}
          >
            <Input
              prefix={<EmailIcon />}
              placeholder="Email"
              className={styles.Personal__form__input}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            label="Your Password"
            name="password"
            rules={[
              {
                required: true,
                validator: (_, value) => passwordValidator(value)
              }
            ]}
          >
            <Input.Password
              prefix={<PassIcon />}
              placeholder="Password"
              className={styles.Personal__form__input}
              iconRender={(visible) => (visible ? <OpenEyeIcon /> : <CloseEyeIcon />)}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item>
            <Flex vertical justify={'center'} align={'center'} gap={'35px'} className={styles.Personal__button}>
              <Flex className={styles.Personal__button__line} align={'center'} gap={'16px'}>
                <span className="divider__line"></span>
                <p>or continue with</p>
                <span className="divider__line"></span>
              </Flex>
              <Flex justify={'center'} gap={'20px'}>
                <GoogleIcon onClick={() => googleLogin()} />
                <AppleSignin
                  authOptions={{
                    clientId: APPLE_CLIENT_ID ?? '',
                    redirectURI: APPLE_REDIRECT_URI ?? '',
                    scope: 'email name',
                    usePopup: true
                  }}
                  className={styles.Personal__button__apple}
                  onSuccess={handleAppleSuccess}
                  onError={handleAppleError}
                  uiType={'dark'}
                  buttonExtraChildren={
                    <Flex>
                      <AppleIcon />
                    </Flex>
                  }
                />
              </Flex>
              <p>
                Already have an account?{' '}
                <Button type={'link'} onClick={() => navigate('/login')}>
                  Sign in
                </Button>
              </p>
              <CustomButton text={'Sign up'} disable={isDisabledBtn} htmlType="submit" />
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </Flex>
  );
};

export default Email;
